import { z } from "zod";
import {
  BOLETO_MAX_CENTS,
  CNAB_LAYOUTS,
  RECONCILE_MAX_TITLES,
  type CnabLayout,
  type TitleSide,
  type TitleStatus,
} from "@cx/domain";
import { listQuery } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import type { PixPaymentView } from "./pix";

// ===== Boleto, CNAB, conciliação OFX e ficha financeira (T2.10, RF-FIN-02/04/05) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);

// ---------- boleto ----------

/** `asaas`/`sandbox` = registrado pelo provedor da integração de pagamentos; `bank_cnab` = carteira própria (remessa). */
export const BOLETO_PROVIDERS = ["sandbox", "asaas", "bank_cnab"] as const;
export type BoletoProvider = (typeof BOLETO_PROVIDERS)[number];

export const boletoCreateInput = z.object({
  titleId: ulidParam,
  /** `integration` = provedor da integração de pagamentos (Asaas/sandbox); `bank` = carteira própria da conta. */
  via: z.enum(["integration", "bank"], "Escolha como registrar o boleto"),
  /** Conta financeira com carteira CNAB configurada (obrigatória em `bank`). */
  financialAccountId: nullish(ulidParam),
  /** Parte do saldo cobrada; ausente = saldo em aberto. */
  amountCents: nullish(
    z
      .number("Informe o valor")
      .int("Use centavos inteiros")
      .min(1, "Informe o valor")
      .max(BOLETO_MAX_CENTS, "Valor acima do limite do boleto"),
  ),
});
export type BoletoCreateInput = z.output<typeof boletoCreateInput>;
export const boletoListQuery = z.object({ titleId: ulidParam });
export const boletoSimulateInput = z.object({
  amountCents: nullish(
    z.number("Informe o valor").int("Use centavos inteiros").min(1, "Informe o valor").max(BOLETO_MAX_CENTS),
  ),
});

/** Situação do registro no banco (carteira própria): da remessa até a confirmação ou baixa. */
export type BoletoBankStatus =
  "pending" | "sent" | "registered" | "rejected" | "cancel_requested" | "cancel_sent" | null;
export type BoletoStatus = "creating" | "active" | "expired" | "paid" | "canceled" | "failed";

export type BoletoView = {
  id: string;
  titleId: string;
  provider: BoletoProvider;
  status: BoletoStatus;
  bankStatus: BoletoBankStatus;
  financialAccountId: string | null;
  amountCents: number;
  principalCents: number;
  interestCents: number;
  fineCents: number;
  paidCents: number;
  dueDate: string | null;
  ourNumber: string | null;
  /** 47 dígitos, já conferidos (DVs). */
  digitableLine: string | null;
  barcode: string | null;
  /** PDF do provedor (Asaas); null = o ERP gera o PDF (`/boletos/{id}/pdf`). */
  url: string | null;
  remittanceId: string | null;
  errorMessage: string | null;
  createdAt: string;
  paidAt: string | null;
  canceledAt: string | null;
  payments: PixPaymentView[];
};

export type BoletoPdfLink = { url: string; fileName: string };

// ---------- carteira CNAB da conta ----------

const digitsField = (n: number, what: string) =>
  z
    .string(`Informe ${what}`)
    .trim()
    .regex(new RegExp(`^\\d{${n}}$`), `${what[0]!.toUpperCase()}${what.slice(1)}: ${n} dígitos`);

export const cnabSettingsInput = z.object({
  layout: z.enum(CNAB_LAYOUTS, "Layout inválido").default("febraban240"),
  agreement: digitsField(7, "o convênio"),
  wallet: digitsField(2, "a carteira"),
  walletVariation: digitsField(3, "a variação da carteira"),
  nextOurNumber: z
    .number("Informe o próximo nosso número")
    .int("Número inválido")
    .min(1, "A partir de 1")
    .max(9_999_999_999, "Até 10 dígitos"),
  nextFileSequence: z
    .number("Informe o próximo número da remessa")
    .int("Número inválido")
    .min(1, "A partir de 1")
    .max(999_999, "Até 6 dígitos"),
  beneficiaryName: text(60),
});
export type CnabSettingsInput = z.output<typeof cnabSettingsInput>;
export type CnabSettingsView = CnabSettingsInput & {
  financialAccountId: string;
  accountName: string;
  bankCode: string | null;
  agency: string | null;
  accountNumber: string | null;
  layout: CnabLayout;
  /** Faltas para gerar boletos e remessas (banco 001, agência, conta, CNPJ da empresa). */
  problems: string[];
};

// ---------- remessas e retornos ----------

export const cnabRemittanceInput = z.object({ financialAccountId: ulidParam });
export const cnabFileListQuery = listQuery.omit({ q: true }).extend({
  financialAccountId: ulidParam.optional(),
  direction: z.enum(["remittance", "return"]).optional(),
});
export type CnabFileListQuery = z.output<typeof cnabFileListQuery>;

export type CnabFileView = {
  id: string;
  financialAccountId: string;
  accountName: string;
  direction: "remittance" | "return";
  layout: CnabLayout;
  sequence: number;
  fileName: string;
  itemsCount: number;
  totalCents: number;
  /** Retorno: ocorrências por situação. */
  applied: number;
  duplicates: number;
  review: number;
  createdAt: string;
};

export type CnabReturnItemStatus = "applied" | "duplicate" | "review" | "info" | "ignored";
export type CnabReturnItemView = {
  id: string;
  line: number;
  movement: string;
  movementLabel: string;
  ourNumber: string;
  paidCents: number;
  feeCents: number;
  occurrenceDate: string | null;
  creditDate: string | null;
  status: CnabReturnItemStatus;
  reason: string | null;
  titleId: string | null;
  contactName: string | null;
};
export type CnabFileDetail = CnabFileView & { items: CnabReturnItemView[]; boletos: BoletoView[] };
export type CnabFileContent = { fileName: string; content: string };

// ---------- extrato OFX e conciliação ----------

export const STATEMENT_LINE_FILTERS = [
  "pending",
  "unmatched",
  "suggested",
  "matched",
  "ignored",
  "all",
] as const;
export const statementLineQuery = listQuery.omit({ q: true }).extend({
  status: z.enum(STATEMENT_LINE_FILTERS, "Filtro inválido").default("pending"),
  from: businessDate.optional(),
  to: businessDate.optional(),
});
export type StatementLineQuery = z.output<typeof statementLineQuery>;

export type StatementImportResult = {
  importId: string;
  total: number;
  imported: number;
  duplicates: number;
  startDate: string | null;
  endDate: string | null;
};

export type StatementLineStatus = "unmatched" | "suggested" | "matched" | "ignored";
export type StatementMatchKind = "titles" | "transaction" | "create" | "ignore";

/** Proposta do Conciliador bancário (agent `reconciler`): um título ou um lançamento já registrado. */
export const reconcileProposal = z.object({
  kind: z.enum(["title", "transaction"]),
  id: ulidParam,
  label: z.string().max(200),
  amountCents: z.number().int(),
  date: z.string().max(10),
});
export type ReconcileProposal = z.infer<typeof reconcileProposal>;

export type ReconcileSuggestionView = {
  id: string;
  proposal: ReconcileProposal;
  rationale: string | null;
  confidencePct4: number | null;
  status: "pending" | "accepted" | "rejected" | "expired";
};

export type StatementLineView = {
  id: string;
  date: string;
  amountCents: number;
  type: string;
  fitId: string;
  memo: string | null;
  name: string | null;
  checkNumber: string | null;
  status: StatementLineStatus;
  matchKind: StatementMatchKind | null;
  titleIds: string[];
  transactionIds: string[];
  /** Todos os lançamentos do casamento estornados: dá para desfazer. */
  canUnmatch: boolean;
  matchedAt: string | null;
  suggestion: ReconcileSuggestionView | null;
};
export type StatementLineList = {
  data: StatementLineView[];
  nextCursor: string | null;
  /** Primeira página: contagem por situação. */
  counts: Record<StatementLineStatus, number> | null;
};

export type ReconcileCandidateView = {
  kind: "title" | "transaction";
  id: string;
  side: TitleSide | null;
  label: string;
  contactName: string | null;
  /** Título: saldo; lançamento: valor com sinal. */
  amountCents: number;
  date: string;
  score: number;
};

export const statementMatchInput = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("titles"),
    titleIds: z
      .array(ulidParam)
      .min(1, "Escolha ao menos uma conta")
      .max(RECONCILE_MAX_TITLES, `No máximo ${RECONCILE_MAX_TITLES} contas`),
  }),
  z.object({ kind: z.literal("transaction"), transactionId: ulidParam }),
  z.object({
    kind: z.literal("create"),
    financialCategoryId: nullish(ulidParam),
    contactId: nullish(ulidParam),
    description: text(200),
  }),
  z.object({ kind: z.literal("ignore") }),
]);
export type StatementMatchInput = z.output<typeof statementMatchInput>;

export type ReconcilerRunResult = { created: number; lines: number };

// ---------- ficha financeira ----------

export const CONTACT_STATEMENT_FILTERS = ["open", "overdue", "paid", "all"] as const;
export const contactStatementQuery = listQuery.omit({ q: true }).extend({
  side: z.enum(["R", "P"], "Lado inválido").default("R"),
  status: z.enum(CONTACT_STATEMENT_FILTERS, "Filtro inválido").default("all"),
});
export type ContactStatementQuery = z.output<typeof contactStatementQuery>;

export type ContactStatementRow = {
  id: string;
  side: TitleSide;
  description: string | null;
  documentNumber: string | null;
  issueDate: string;
  dueDate: string;
  amountCents: number;
  paidCents: number;
  openCents: number;
  status: TitleStatus;
  overdue: boolean;
  lastPaidAt: string | null;
};

export type ContactStatement = {
  contact: { id: string; name: string; document: string | null };
  side: TitleSide;
  totals: {
    openCents: number | null;
    overdueCents: number | null;
    paidCents: number | null;
    openCount: number;
    overdueCount: number;
    paidCount: number;
  };
  /** Só no lado a receber. */
  credit: {
    mode: "unlimited" | "zero" | "value";
    limitCents: number | null;
    usedCents: number | null;
    availableCents: number | null;
    exceeded: boolean;
  } | null;
  data: ContactStatementRow[];
  nextCursor: string | null;
};
